import React, {useMemo} from 'react';

import {CopyButton} from 'src/components/copy-button';
import {AccountLink, UserLink} from 'src/components/links';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {SECTION_NAME} from 'src/modules/sso-accounts/constants';

import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {ssoDraftDetails, ssoDraftDetailsColumns, ssoDraftDetailsRow} from '../sso-settings-profile.module.styl';
import {formatBoolean, formatEnumValue} from '../utils/format';

import {SsoTimestampCell} from './sso-timestamp-cell';

import type {DetailItem, SsoDraftDetailsProps as Props} from '../typedef';

export const SsoDraftDetails = ({
    draft,
    notificationStatistics,
    deleteAction,
    postponeAction,
    headerStickToElement,
}: Props) => {
    const details = useMemo<DetailItem[]>(() => {
        const accountName = draft.accountName || String(draft.accountId);
        const notificationDetails: DetailItem[] = notificationStatistics ? [
            ['Notifications Sent', notificationStatistics.totalNotifications],
            ['Times Postponed', notificationStatistics.totalPostponedDrafts],
        ] : [];

        return [
            ['Account Name', (
                <Group gap="xs" key="account-name">
                    <AccountLink
                        accountId={draft.accountId}
                        accountName={accountName}
                        place={SECTION_NAME}
                        blank={true}
                    />
                    <CopyButton copiedText={accountName} entityName="Account Name"/>
                </Group>
            )],
            ['Account ID', draft.accountId],
            ['Current Step', formatEnumValue(draft.currentStep)],
            ['Latest Step', formatEnumValue(draft.latestStep)],
            ['Status', formatEnumValue(draft.status)],
            ['Identity Provider', formatEnumValue(draft.provider)],
            ['Domains', draft.domains?.length ? draft.domains.join(', ') : '--'],
            ['Domain Validation', formatEnumValue(draft.domainValidationStatus)],
            ['Metadata Validation', formatEnumValue(draft.metadataValidationStatus)],
            ['Certificate Validation', formatEnumValue(draft.certValidationStatus)],
            ['Login Fields Confirmed', formatBoolean(draft.loginFieldsConfirmed)],
            ['Test Confirmed', formatBoolean(draft.testConfirmed)],
            ['SSO Mandatory', formatBoolean(draft.ssoMandatory)],
            ['SSO Enabled', formatBoolean(draft.ssoEnabled)],
            ['Created', <SsoTimestampCell timestamp={draft.createdAt} key="created"/>],
            ['Created By', (
                <UserLink
                    key="created-by"
                    userId={draft.createdBy}
                    caption={draft.creatorEmail || String(draft.createdBy)}
                    place={SECTION_NAME}
                />
            )],
            ['Updated', <SsoTimestampCell timestamp={draft.updatedAt} key="updated"/>],
            ['Updated By', draft.updatedBy !== undefined ? (
                <UserLink
                    key="updated-by"
                    userId={draft.updatedBy}
                    caption={draft.updatorEmail || String(draft.updatedBy)}
                    place={SECTION_NAME}
                />
            ) : '--'],
            ['Finished', <SsoTimestampCell timestamp={draft.finishedAt} key="finished"/>],
            ...notificationDetails,
        ];
    }, [draft, notificationStatistics]);
    const errors = Object.entries(draft.errors || {});

    const mid = Math.ceil(details.length / 2);
    const leftDetails = details.slice(0, mid);
    const rightDetails = details.slice(mid);

    const renderColumn = (items: Array<[string, React.ReactNode]>, includeErrors?: boolean) => (
        <dl>
            {items.map(([label, value]) => (
                <div className={ssoDraftDetailsRow} key={String(label)}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                </div>
            ))}
            {includeErrors ? (
                <div className={ssoDraftDetailsRow}>
                    <dt>Errors</dt>
                    <dd>
                        {errors.length ? errors.map(([field, message]) => (
                            <div key={field}><strong>{formatEnumValue(field)}:</strong> {message}</div>
                        )) : '--'}
                    </dd>
                </div>
            ) : null}
        </dl>
    );

    return (
        <section className={ssoDraftDetails}>
            <ProfileMain
                stickToElement={headerStickToElement}
                header={(
                    <Item grow={1}>
                        <Group justify="space-between" alignItems="center">
                            <Typography fontWeight="semi-bold" variant="heading-level-3">
                                Customer Admin Settings
                            </Typography>
                            <Group gap="xs">
                                {postponeAction}
                                {deleteAction}
                            </Group>
                        </Group>
                    </Item>
                )}>
                <div className={ssoDraftDetailsColumns}>
                    {renderColumn(leftDetails)}
                    {renderColumn(rightDetails, true)}
                </div>
            </ProfileMain>
        </section>
    );
};
